import React, { useEffect, useState } from "react";
import { SmallText } from "../../components/core/utility";
import { CustomisedMultiDropdown } from "../EditVehicleWTA/AddMaterial";
import "../EditVehicleWTA/addmaterial.css";
import { Button } from "@vds/buttons";
import { DropdownSelect } from "@vds/selects";
import { getFilterValues, getReturnableItemDetails } from "../../services/C4S Decommission - Replacement/returnReship";
import { messageBus } from "../../../utility/messageBus";

const tableHeader = [
  {
    name: "ESN",
    width: "7%",
  },
  {
    name: "Hardware",
    width: "10%",
  },
  {
    name: "Bundle ID",
    width: "5%",
  },
  {
    name: "Subscription",
    width: "10%",
  },
  {
    name: "Tech Ref ID",
    width: "7%",
  },

  {
    name: "Workticket",
    width: "5%",
  },
  {
    name: "Shipping Address",
    width: "7%",
  },
  {
    name: "Contact",
    width: "5%",
  },
];

const getAddressLabel = (addr) => {
  return `${addr.address1}${addr.address2 ? ` ${addr.address2}` : ""} ${addr.city} ${addr.stateCode} ${addr.zipCode} ${addr.country}`;
};

const SelectionMaterial = ({ onReturnOrReshipAction }) => {
  const [appliedFilters, setAppliedFilters] = useState({
    workticket: [],
    hardware: [],
    esn: [],
    address: [],
  });

  const [workticketData, setWorkticketData] = useState([]);
  const [hardwareData, setHardwareData] = useState([]);
  const [esnData, setEsnData] = useState([]);
  const [addressData, setAddressData] = useState([]);

  const [selectedRows, setSelectedRows] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [lastAppliedFilters, setLastAppliedFilters] = useState(null);

  const cspSessionData = JSON.parse(sessionStorage.getItem("CSPDetails"));
  const providerOrder = cspSessionData?.CSP.length ? cspSessionData?.CSP.filter((csp) => csp.DefaultCSP === "1")[0].Provider_Order : "";
  // NEW: derive full objects for selected rows
  const selectedRowObjects = React.useMemo(() => selectedRows.map((i) => tableData[i]).filter(Boolean), [selectedRows, tableData]);

  const isAllSelected = React.useMemo(() => tableData.length > 0 && selectedRows.length === tableData.length, [selectedRows, tableData]);

  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedRows([]);
    } else {
      setSelectedRows(tableData.map((_, idx) => idx));
    }
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      // Fetch initial data for workticket, hardware, esn, and address
      const response = await getFilterValues(providerOrder);
      const { returnableWorkTickets, returnableHardwareIds, returnableEsns, returnableAddresses } = response.data.data;
      setWorkticketData(returnableWorkTickets);
      setHardwareData(Object.keys(returnableHardwareIds).map((key) => ({ id: key, name: `${returnableHardwareIds[key]} - ${key}` })));
      setEsnData(returnableEsns);
      setAddressData(
        returnableAddresses.map((addr) => {
          return {
            ...addr,
            label: getAddressLabel(addr),
          };
        })
      );
    };
    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const resetHandler = () => {
      setAppliedFilters({ workticket: [], hardware: [], esn: [], address: [] });
      setTableData([]);
      setSelectedRows([]);
      setLastAppliedFilters(null);
    };
    messageBus.on("resetFilters", resetHandler);
    return () => {
      messageBus.off("resetFilters", resetHandler);
    };
  }, []);

  // Determine which dropdown is currently the primary filter (matches query build priority)
  const getPrimaryFilterKey = (filters) => {
    if (!filters) return null;
    if (Array.isArray(filters.workticket) && filters.workticket.length) return "workticket";
    if (Array.isArray(filters.hardware) && filters.hardware.length) return "hardware";
    if (Array.isArray(filters.esn) && filters.esn.length) return "esn";
    if (Array.isArray(filters.address) && filters.address.length) return "address";
    return null;
  };

  useEffect(() => {
    // Only clear data when user switches to a different dropdown as the primary filter
    // If they update values within the same dropdown, keep current table until Apply
    const lastPrimary = getPrimaryFilterKey(lastAppliedFilters);
    const currentPrimary = getPrimaryFilterKey(appliedFilters);
    if (lastPrimary && currentPrimary && lastPrimary !== currentPrimary) {
      setTableData([]);
      setSelectedRows([]);
    }
  }, [appliedFilters, lastAppliedFilters]);

  const hasAppliedFilters = React.useMemo(
    () =>
      Object.keys(appliedFilters).length > 0 &&
      Object.values(appliedFilters).some((v) => (Array.isArray(v) ? v.length > 0 : v !== undefined && v !== null && v !== "")),
    [appliedFilters]
  );

  const canApply = React.useMemo(() => {
    if (!hasAppliedFilters) return false;
    if (lastAppliedFilters === null) return true; // first time
    return JSON.stringify(appliedFilters) !== JSON.stringify(lastAppliedFilters);
  }, [appliedFilters, lastAppliedFilters, hasAppliedFilters]);

  const onApplyClick = async () => {
    // Reset selection and table data if any rows are selected before applying filters
    if (selectedRows.length > 0) {
      setSelectedRows([]);
      setTableData([]);
    }

    let query = `providerOrder=${providerOrder}&`;
    if (appliedFilters.workticket.length) {
      query += `workTicketIds=${appliedFilters.workticket.join(",")}`;
    } else if (appliedFilters.hardware.length) {
      query += `hardwareIds=${appliedFilters.hardware.join(",")}`;
    } else if (appliedFilters.esn.length) {
      query += `esns=${appliedFilters.esn.join(",")}`;
    } else if (appliedFilters.address.length) {
      query += `addressIds=${appliedFilters.address.join(",")}`;
    }
    const response = await getReturnableItemDetails(query);
    setTableData(response.data.data || []);
    setLastAppliedFilters(appliedFilters);
  };

  return (
    <div>
      {/* filters and buttons */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", marginBottom: "16px", marginTop: "24px" }}>
        <div style={{ flex: "1 1 22%", maxWidth: "181px" }}>
          <SmallText>Work Ticket ID</SmallText>
          <CustomisedMultiDropdown
            inputId="workticket-multiselect"
            panelClassName="add-material-modal-multiselect"
            panelStyle={{ width: 250 }}
            value={appliedFilters.workticket || []}
            options={
              workticketData.map((e) => {
                return { label: e, workticketId: e };
              }) || []
            }
            onChange={(e) =>
              setAppliedFilters((prev) => {
                return { ...prev, workticket: e.value };
              })
            }
            placeholder="Select Workticket ID"
            filter
            style={{ width: "100%" }}
            disabled={hasAppliedFilters && !appliedFilters.workticket.length}
            optionLabel="label"
            optionValue="workticketId"
            maxSelectedLabels={0}
            filterPlaceholder="Search Workticket ID"
            filterInputAutoFocus={false}
            filterBy="workticketId"
            showSelectAll={false}
            emptyMessage="No worktickets available"
            itemTemplate={(opt) => <div style={{ width: "100%", padding: "4px 8px" }}>{opt.label}</div>}
          />
        </div>
        <div style={{ flex: "1 1 22%", maxWidth: "181px" }}>
          <SmallText>Hardware ID</SmallText>
          <CustomisedMultiDropdown
            inputId="hardware-multiselect"
            panelClassName="add-material-modal-multiselect"
            panelStyle={{ width: 300 }}
            value={appliedFilters.hardware || []}
            options={hardwareData}
            onChange={(e) =>
              setAppliedFilters((prev) => {
                return { ...prev, hardware: e.value };
              })
            }
            disabled={hasAppliedFilters && !appliedFilters.hardware.length}
            placeholder="Select Hardware ID"
            filter
            style={{ width: "100%" }}
            optionLabel="name"
            optionValue="id"
            maxSelectedLabels={0}
            filterPlaceholder="Search Hardware ID"
            filterInputAutoFocus={false}
            filterBy="name"
            showSelectAll={false}
            emptyMessage="No Hardwares available"
          />
        </div>
        <div style={{ flex: "1 1 22%", maxWidth: "181px" }}>
          <SmallText>ESN</SmallText>
          <CustomisedMultiDropdown
            inputId="esn-multiselect"
            panelClassName="add-material-modal-multiselect"
            panelStyle={{ width: 250 }}
            value={appliedFilters.esn || []}
            options={
              esnData.map((e) => {
                return { label: e, esn: e };
              }) || []
            }
            onChange={(e) =>
              setAppliedFilters((prev) => {
                return { ...prev, esn: e.value };
              })
            }
            disabled={hasAppliedFilters > 0 && !appliedFilters.esn.length}
            placeholder="Select ESN"
            filter
            style={{ width: "100%" }}
            optionLabel="label"
            optionValue="esn"
            maxSelectedLabels={0}
            filterPlaceholder="Search ESN"
            filterInputAutoFocus={false}
            filterBy="esn"
            showSelectAll={false}
            emptyMessage="No ESNs available"
          />
        </div>
        <div style={{ flex: "1 1 22%", maxWidth: "181px" }}>
          <SmallText>Address</SmallText>
          <CustomisedMultiDropdown
            inputId="address-multiselect"
            panelClassName="add-material-modal-multiselect"
            value={appliedFilters.address || []}
            options={addressData}
            onChange={(e) =>
              setAppliedFilters((prev) => {
                return { ...prev, address: e.value };
              })
            }
            disabled={hasAppliedFilters && !appliedFilters.address.length}
            placeholder="Select Address"
            filter
            style={{ width: "100%" }}
            optionLabel="label"
            optionValue="addressId"
            maxSelectedLabels={0}
            filterPlaceholder="Search Address"
            filterInputAutoFocus={false}
            filterBy="label"
            showSelectAll={false}
            emptyMessage="No Address available"
          />
        </div>
        {/*  disable once after making api call and enable once after change in filter */}
        <Button primary disabled={!canApply} onClick={onApplyClick} style={{ marginTop: "auto" }}>
          Apply
        </Button>
        {hasAppliedFilters ? (
          <div
            style={{
              fontFamily: "Verizon-NHG-eDS",
              fontWeight: 400,
              fontSize: "16px",
              textDecoration: "underline",
              textDecorationStyle: "solid",
              textDecorationOffset: "0%",
              textDecorationThickness: "0%",
              textDecorationSkipInk: "auto",
              marginTop: "auto",
              cursor: "pointer",
            }}
            onClick={() => {
              setAppliedFilters({ workticket: [], hardware: [], esn: [], address: [] });
              setTableData([]);
              setSelectedRows([]);
              setLastAppliedFilters(null);
            }}
          >
            Clear Filter
          </div>
        ) : (
          <></>
        )}
        <div style={{ width: "181px", marginLeft: "auto" }}>
          <DropdownSelect
            name="select-action"
            label="Select Action"
            value="Select Action"
            onChange={(e) => {
              // onReturnOrReshipAction(
              //   e.target.value,
              //   selectedRows.map((i) => tableData[i])
              // );
              onReturnOrReshipAction(e.target.value, selectedRowObjects);
            }}
            disabled={selectedRows.length === 0}
          >
            <option disabled>Select Action</option>
            {[
              { label: "Return Only After Cancellation", value: "return" },
              { label: "Return / Reship", value: "return_reship" },
            ].map((m) => {
              return (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              );
            })}
          </DropdownSelect>
        </div>
      </div>
      {/* table */}
      <div
        style={{
          fontFamily: "Verizon-NHG-eDS",
          fontWeight: 700,
          fontStyle: "Bold",
          fontSize: "20px",
          lineHeight: "24px",
          color: "#000000",
          marginTop: "24px",
          marginBottom: "16px",
        }}
      >
        Return / Reship
      </div>
      <table style={{ borderSpacing: "0px", fontFamily: "Verizon-NHG-eTX, Helvetica, Arial, sans-serif" }}>
        <thead>
          <tr style={{ textAlign: "left" }}>
            {/* Select All checkbox header */}
            <th style={{ width: "4%", padding: "10px", borderBottom: "1px solid black" }}>
              <input
                type="checkbox"
                aria-label="Select All"
                style={{ width: "20px", height: "20px", accentColor: "#000", cursor: tableData.length ? "pointer" : "not-allowed" }}
                checked={isAllSelected}
                onChange={toggleSelectAll}
                disabled={!(tableData.length > 0 && hasAppliedFilters)}
              />
            </th>
            {tableHeader.map((header) => (
              <th key={header.name} style={{ width: header.width, padding: "10px", borderBottom: "1px solid black" }}>
                <SmallText bold>{header.name}</SmallText>
              </th>
            ))}
          </tr>
        </thead>
        <tbody style={{ textAlign: "left", verticalAlign: "top" }}>
          {tableData.length > 0 && hasAppliedFilters ? (
            tableData.map((row, index) => (
              <tr key={index} style={{ background: index % 2 === 0 ? "#D9D9D9" : "#F6F6F6" }}>
                {/* Row checkbox cell */}
                <td style={{ padding: "8px", width: "4%" }}>
                  <input
                    type="checkbox"
                    aria-label={`Select row ${index + 1}`}
                    style={{ width: "20px", height: "20px", marginRight: "8px", accentColor: "#000", cursor: "pointer" }}
                    checked={selectedRows.includes(index)}
                    onChange={() => {
                      setSelectedRows((prev) => (prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]));
                    }}
                  />
                </td>
                <td style={{ padding: "8px", width: "7%" }}>{row.esn}</td>
                <td style={{ padding: "8px", width: "10%" }}>
                  <div>{row.hardwareName}</div>
                  <div>{row.hardwareId}</div>
                </td>
                <td style={{ padding: "8px", width: "5%" }}>{row.bundleId}</td>
                <td style={{ padding: "8px", width: "10%" }}>
                  <div>{row.subscriptionName}</div> <div>{row.subscriptionId}</div>
                </td>
                <td style={{ padding: "8px", width: "7%" }}>{row.techRefId}</td>
                <td style={{ padding: "8px", width: "5%" }}>
                  <div>{row.workTicketId}</div>
                  <div>{row.wtStatus}</div>
                </td>
                <td style={{ padding: "8px", width: "7%" }}>{getAddressLabel(row.shippingAddress)}</td>
                <td style={{ padding: "8px", width: "5%" }}>{row.contact.customerName}</td>
              </tr>
            ))
          ) : (
            <tr style={{ background: "#F6F6F6", textAlign: "center" }}>
              <td colSpan={tableHeader.length + 1} style={{ padding: "16px" }}>
                NO DATA
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default SelectionMaterial;
